/**
 * 관리자 가격 플랜 등록/수정 모달.
 * - 새 플랜: initial=undefined → POST /pricing-plans
 * - 수정: initial 채워짐 → PATCH /pricing-plans/:id
 *
 * features 는 줄바꿈 구분 textarea — UI 단순함 vs JSON 입력 부담 트레이드오프.
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  createPricingPlan,
  updatePricingPlan,
  type PricingPlan,
} from "@/lib/console-api";
import { extractErrorMessage } from "@/lib/api-client";

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: (plan: PricingPlan) => void;
  initial?: PricingPlan;
}

export function PricingPlanFormDialog({
  open,
  onClose,
  onSaved,
  initial,
}: Props): JSX.Element | null {
  const isEdit = Boolean(initial);
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [priceCents, setPriceCents] = useState(0);
  const [billingPeriod, setBillingPeriod] = useState("month");
  const [isPerSeat, setIsPerSeat] = useState(false);
  const [description, setDescription] = useState("");
  const [featuresText, setFeaturesText] = useState("");
  const [sortOrder, setSortOrder] = useState(100);
  const [active, setActive] = useState(true);
  const [highlighted, setHighlighted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const slugRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setSlug(initial?.slug ?? "");
    setName(initial?.name ?? "");
    setPriceCents(initial?.priceCents ?? 0);
    setBillingPeriod(initial?.billingPeriod ?? "month");
    setIsPerSeat(initial?.isPerSeat ?? false);
    setDescription(initial?.description ?? "");
    setFeaturesText((initial?.features ?? []).join("\n"));
    setSortOrder(initial?.sortOrder ?? 100);
    setActive(initial?.active ?? true);
    setHighlighted(initial?.highlighted ?? false);
    setError(null);
    setSubmitting(false);
    setTimeout(() => (isEdit ? nameRef : slugRef).current?.focus(), 0);
  }, [open, initial, isEdit]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const features = featuresText
      .split("\n")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    try {
      const saved = isEdit
        ? await updatePricingPlan(initial!.id, {
            name,
            priceCents,
            billingPeriod,
            isPerSeat,
            description: description.trim() || null,
            features,
            sortOrder,
            active,
            highlighted,
          })
        : await createPricingPlan({
            slug,
            name,
            priceCents,
            billingPeriod,
            isPerSeat,
            ...(description.trim() ? { description: description.trim() } : {}),
            features,
            sortOrder,
            active,
            highlighted,
          });
      onSaved(saved);
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err, "저장에 실패했습니다"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-lg">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">
              {isEdit ? "플랜 수정" : "새 플랜"}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              가격 / 기능을 변경하면 랜딩 /pricing 에 즉시 반영됩니다.
            </p>
          </div>
          <button
            type="button"
            aria-label="닫기"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="plan-slug" className="text-sm font-medium">
                Slug
              </label>
              <Input
                id="plan-slug"
                ref={slugRef}
                type="text"
                required
                disabled={isEdit}
                placeholder="team"
                className="mt-1.5 font-mono"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
              />
              {isEdit && (
                <p className="mt-1 text-xs text-muted-foreground">
                  slug 은 등록 후 변경 불가
                </p>
              )}
            </div>
            <div>
              <label htmlFor="plan-name" className="text-sm font-medium">
                이름
              </label>
              <Input
                id="plan-name"
                ref={nameRef}
                type="text"
                required
                placeholder="Team"
                className="mt-1.5"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label htmlFor="plan-price" className="text-sm font-medium">
                가격 (cents)
              </label>
              <Input
                id="plan-price"
                type="number"
                min={0}
                required
                className="mt-1.5"
                value={priceCents}
                onChange={(e) => setPriceCents(Number(e.target.value) || 0)}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {priceCents === 0
                  ? "무료"
                  : `$${(priceCents / 100).toFixed(2)}`}
              </p>
            </div>
            <div>
              <label htmlFor="plan-period" className="text-sm font-medium">
                주기
              </label>
              <select
                id="plan-period"
                className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={billingPeriod}
                onChange={(e) => setBillingPeriod(e.target.value)}
              >
                <option value="month">월</option>
                <option value="year">년</option>
              </select>
            </div>
            <div>
              <label htmlFor="plan-sort" className="text-sm font-medium">
                정렬 순서
              </label>
              <Input
                id="plan-sort"
                type="number"
                className="mt-1.5"
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value) || 100)}
              />
            </div>
          </div>
          <div>
            <label htmlFor="plan-desc" className="text-sm font-medium">
              한 줄 설명
            </label>
            <Input
              id="plan-desc"
              type="text"
              maxLength={200}
              className="mt-1.5"
              placeholder="팀이 나눠 일하고, 결과는 한 PR 로"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="plan-features" className="text-sm font-medium">
              기능 (한 줄에 하나씩)
            </label>
            <textarea
              id="plan-features"
              className="mt-1.5 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              rows={6}
              placeholder={"멤버별 태스크 분담\nAI 코드 리뷰\n통합 시점 build 검증"}
              value={featuresText}
              onChange={(e) => setFeaturesText(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={isPerSeat}
                onChange={(e) => setIsPerSeat(e.target.checked)}
                className="accent-primary"
              />
              좌석당 (per-seat)
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="accent-primary"
              />
              랜딩에 노출
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={highlighted}
                onChange={(e) => setHighlighted(e.target.checked)}
                className="accent-primary"
              />
              "추천" 배지
            </label>
          </div>
          {error && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              취소
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? "저장" : "등록"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
